import { t } from "ftc-common/i18n";
import {useAsset} from "ftc-common/AssetHelper";
import { Alert, Button } from "react-bootstrap";
import { useEffect, useState } from "react";

const FLL_SCORE_CALC_RESPONSIVE_TOUCH_CSS = `
.fll-score-calculator-touch {
  width: 80%;
  max-width: 100%;
}
.fll-score-calc-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}
.fll-score-calc-row-label {
  flex: 1 1 auto;
  min-width: 0;
}
.fll-score-calc-row-controls {
  flex: 0 0 auto;
}
.fll-score-calc-stepper {
  display: inline-flex;
  flex-flow: row nowrap;
  align-items: stretch;
  gap: 0.35rem;
}
.fll-score-calc-stepper-btn {
  flex: 0 0 auto;
  width: 2.75rem;
  min-width: 2.75rem;
  height: 2.75rem;
  min-height: 2.75rem;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  line-height: 1;
}
.fll-score-calc-stepper-input {
  flex: 1 1 auto;
  width: clamp(2.5rem, 10vw, 5rem);
  min-width: 2.5rem;
  max-width: 5rem;
  min-height: 2.75rem;
  text-align: center;
  appearance: textfield;
  -moz-appearance: textfield;
}
.fll-score-calc-stepper-input::-webkit-inner-spin-button,
.fll-score-calc-stepper-input::-webkit-outer-spin-button {
  margin: 0;
  -webkit-appearance: none;
}
.fll-score-calculator-touch .btn-group > .btn {
  min-width: 2.75rem;
  min-height: 2.75rem;
  padding: 0 0.65rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
@media (max-width: 991.98px) {
  .fll-score-calculator-touch .btn:not(.btn-close):not(.fll-score-calc-stepper-btn) {
    min-height: 2.75rem;
    padding-top: 0.45rem;
    padding-bottom: 0.45rem;
    font-size: 0.95rem;
  }
  .fll-score-calculator-touch .btn-sm:not(.btn-close):not(.fll-score-calc-stepper-btn) {
    min-height: 2.75rem;
    min-width: 2.75rem;
    font-size: 0.95rem;
  }
  .fll-score-calculator-touch .btn-group > .btn {
    min-height: 2.75rem;
    min-width: 2.75rem;
  }
  .fll-score-calculator-touch .form-control-sm:not(.fll-score-calc-stepper-input) {
    min-height: 2.75rem;
    font-size: 1rem;
  }
}
@media (max-width: 575.98px) {
  .fll-score-calculator-touch .btn-group {
    justify-content: flex-start;
  }
  .fll-score-calc-row {
    flex-wrap: wrap;
  }
  .fll-score-calc-row-controls {
    width: 100%;
    display: flex;
    justify-content: flex-end;
  }
  .fll-score-calc-sticky-bar__title {
    font-size: 1rem;
  }
  .fll-score-calc-sticky-bar__actions {
    flex-wrap: wrap;
    justify-content: flex-end;
  }
}
.fll-score-calc-sticky-bar {
  position: sticky;
  top: 0;
  z-index: 1030;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 0.75rem;
  padding: 0.65rem 1rem;
  background: rgba(255, 255, 255, 0.97);
  border: 1px solid #e9ecef;
  border-radius: 0.75rem;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.12);
  backdrop-filter: blur(6px);
}
.fll-score-calc-sticky-bar__title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #111827;
  line-height: 1.2;
}
.fll-score-calc-sticky-bar__actions {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-left: auto;
}
.fll-score-calc-score-box {
  min-width: 5.5rem;
  padding: 0.25rem 0.85rem;
  text-align: center;
  border-radius: 0.5rem;
  border: 2px solid #ffc107;
  background: #fff;
}
.fll-score-calc-score-box--stale {
  border-color: #f59e0b;
  background: #fffbeb;
}
.fll-score-calc-score-box__label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
  line-height: 1.1;
}
.fll-score-calc-score-box__value {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.15;
  color: #111827;
}
.fll-score-calc-score-box__hint {
  font-size: 0.65rem;
  color: #b45309;
  font-weight: 600;
  line-height: 1.1;
}
.fll-score-calc-hidden-meta-spacer { height: 1.25rem; }
.fll-gp-panel__selection-row { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 0.75rem; }
.fll-gp-panel__levels { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 0.5rem; }
`;

type LinearCriterion = {
    id: string;
    type: "linear";
    label: string;
    pointsPerUnit: number;
    maxQuantity: number;
    terminalOptionLabel?: string;
};

type BooleanCriterion = {
    id: string;
    type: "boolean";
    label: string;
    points: number;
};

type BonusCondition = {
    field: string;
    operator: ">=" | "==" ;
    value: number | boolean;
};

type BonusCriterion = {
    id: string;
    type: "bonus";
    label: string;
    description: string;
    points: number;
    allOf: BonusCondition[];
    response?: {
        id: string;
        enabledWhen: BonusCondition;
        inactiveLabel: string;
        activeLabel: string;
        helpText: string;
    };
};

type Penalty = {
    threshold: number;
    points?: number;
    special?: "MATCH_ZERO";
    label: string;
};

type PenaltyCriterion = {
    id: string;
    type: "penalty_tier";
    label: string;
    maxQuantity: number;
    tiers: Penalty[];
};

type Criterion = LinearCriterion | BooleanCriterion | BonusCriterion | PenaltyCriterion;

type Section = {
    id: string;
    title: string;
    intro?: string;
    criteria: Criterion[];
};

type GraciousProfessionalism = {
    heading: string;
    description: string;
    levels: Array<{ label: string; value: number }>;
};

type CalculatorConfig = {
    season: string;
    version: number;
    presentation: {
        showPointValues: boolean;
        showMaximumValues: boolean;
        graciousProfessionalism: GraciousProfessionalism;
    };
    sections: Section[];
};

type ScoreValue = number | boolean;
type ScoreValues = Record<string, ScoreValue>;

function getDefaultValues(): ScoreValues {
    return {};
}

function evaluateCondition(values: ScoreValues, c: BonusCondition): boolean {
    const current = values[c.field];
    if (c.operator === ">=") return Number(current ?? 0) >= Number(c.value);
    if (c.operator === "==") return current === c.value;
    return false;
}

function missionHeaderParts(title: string): { number: string; name: string } {
    const match = title.match(/^(Mission\s+\d+):\s*(.*)$/i);
    if (!match) {
        return { number: title, name: "" };
    }
    return { number: match[1], name: match[2] };
}

export function FllScoreCalculator() {
    const fllLogo = useAsset("img/FLL_logo_horiz.png");
    const bioglow = useAsset("img/logos/bioglow.png");
    const [config, setConfig] = useState<CalculatorConfig | null>(null);
    const [scores, setScores] = useState<ScoreValues>({});
    const [total, setTotal] = useState<number | null>(null);
    const [gpSelection, setGpSelection] = useState<number | null>(null);
    const [isDirty, setIsDirty] = useState<boolean>(false);
    const [isCalculating, setIsCalculating] = useState<boolean>(false);
    const [loadingError, setLoadingError] = useState<string | null>(null);

    useEffect(() => {
        fetch("/fll/score-calculator/config")
            .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
            .then((cfg: CalculatorConfig) => {
                setConfig(cfg);
                const defaults = getDefaultValues();
                setScores(defaults);
            })
            .catch(() => setLoadingError(t`Unable to load calculator rules from server.`));
    }, []);

    const m01BonusResponse = config?.sections
        .find(section => section.id === "M01")
        ?.criteria.find((criterion): criterion is BonusCriterion => criterion.type === "bonus")
        ?.response;
    const queenEnabled = m01BonusResponse ? evaluateCondition(scores, m01BonusResponse.enabledWhen) : false;
    const queenChecked = m01BonusResponse ? scores[m01BonusResponse.id] === true : false;

    useEffect(() => {
        if (!queenEnabled && queenChecked) {
            setScores(was => ({ ...was, [m01BonusResponse!.id]: false }));
            setIsDirty(true);
        }
    }, [m01BonusResponse, queenEnabled, queenChecked]);

    if (loadingError) {
        return <Alert variant={"danger"}>{loadingError}</Alert>;
    }
    if (!config) {
        return <Alert variant={"info"}>{t`Loading score calculator...`}</Alert>;
    }

    const defaults = getDefaultValues();

    const radioOptionsForMax = (maxQuantity: number, terminalOptionLabel?: string): Array<{ label: string; value: number }> => {
        const options: Array<{ label: string; value: number }> = [];
        for (let i = 0; i < maxQuantity; i++) {
            options.push({ label: String(i), value: i });
        }
        options.push({ label: terminalOptionLabel ?? `${maxQuantity}+`, value: maxQuantity });
        return options;
    };

    const onLinearChange = (id: string, value: number, maxQuantity: number) => {
        const normalizedValue = Math.max(0, Math.min(maxQuantity, Number.isFinite(value) ? value : 0));
        setScores(was => ({ ...was, [id]: normalizedValue }));
        setIsDirty(true);
    };

    const onNumericInputChange = (id: string, rawValue: string, maxQuantity: number) => {
        const digits = rawValue.replace(/\D/g, "");
        onLinearChange(id, digits ? Number(digits) : 0, maxQuantity);
    };

    const onBooleanChange = (id: string, value: boolean) => {
        setScores(was => ({ ...was, [id]: value }));
        setIsDirty(true);
    };

    const triggerCalculate = () => {
        setIsCalculating(true);
        fetch("/fll/score-calculator/calculate", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ values: scores })
        })
            .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
            .then((payload: { totalScore: number }) => {
                setTotal(Number(payload.totalScore ?? 0));
                setIsDirty(false);
            })
            .catch(() => setLoadingError(t`Unable to calculate score right now.`))
            .finally(() => setIsCalculating(false));
    };

    return <>
        <style>{FLL_SCORE_CALC_RESPONSIVE_TOUCH_CSS}</style>
        <div className="px-3 py-3 mx-auto my-3 fll-score-calculator-touch" style={{ maxWidth: 1200 }}>
            <div
                className="card shadow-lg border border-2 rounded-4 mb-3"
                style={{ backgroundColor: "#ffffffeb" }}
            >
                <div className="d-flex align-items-center justify-content-between w-100 border-bottom pb-2 px-3 pt-3">
                    <div className="d-flex align-items-center gap-2 gap-md-3">
                        <img src={fllLogo} alt="FIRST Lego League"
                             style={{ height: 80 }}
                             className="d-inline-block"/>
                        <img src={bioglow} alt="BIOGLOW"
                             style={{ height: 80 }}
                             className="d-inline-block"/>
                    </div>
                    <a
                        href="https://www.firstinspires.org/report"
                        target="_blank"
                        rel="noreferrer"
                        className="text-muted text-nowrap ms-3"
                    >
                        {t`Report a Concern`}
                    </a>
                </div>
            </div>

            <div className="fll-score-calc-sticky-bar" role="region" aria-label={t`Score summary`}>
                <h1 className="fll-score-calc-sticky-bar__title">{t`Official Scoring Calculator`}</h1>
                <div className="fll-score-calc-sticky-bar__actions">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => {
                            setScores(defaults);
                            setTotal(null);
                            setGpSelection(null);
                            setIsDirty(false);
                        }}
                    >
                        {t`Start Over`}
                    </Button>
                    <div
                        className={`fll-score-calc-score-box${isDirty ? " fll-score-calc-score-box--stale" : ""}`}
                        aria-live="polite"
                        aria-atomic="true"
                    >
                        <div className="fll-score-calc-score-box__label">{t`Score`}</div>
                        <div className="fll-score-calc-score-box__value">{total === null ? "—" : total}</div>
                        {isDirty ? (
                            <div className="fll-score-calc-score-box__hint">{t`Calculation Needed`}</div>
                        ) : null}
                    </div>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={triggerCalculate}
                        disabled={!isDirty || isCalculating}
                    >
                        {isCalculating ? t`Calculating…` : t`Calculate score`}
                    </Button>
                </div>
            </div>

            <div
                className="card shadow-lg border border-2 rounded-4 px-3 pb-3"
                style={{ backgroundColor: "#ffffffeb" }}
            >
                <div className="pt-2 pb-1">
                    {config.sections.map(section => {
                        const header = missionHeaderParts(section.title);
                        return (
                            <div key={section.id} className="card mb-3">
                                <div className="card-body">
                                    <div className="mb-2">
                                        <div className="bg-dark text-white px-2 py-1 fw-bold d-inline-block">{header.number}</div>
                                    </div>
                                    <div>
                                        <h3 className="mb-3">{header.name || section.title}</h3>
                                        {section.intro ? <div className="fw-semibold mb-3">{section.intro}</div> : null}
                                        {section.criteria.map(criterion => {
                                            if (criterion.type === "bonus") {
                                                const response = criterion.response;
                                                const responseEnabled = response ? evaluateCondition(scores, response.enabledWhen) : false;
                                                const responseValue = response ? scores[response.id] === true : false;
                                                const responseHasValue = response ? Object.prototype.hasOwnProperty.call(scores, response.id) : false;
                                                return (
                                                    <div key={criterion.id} className="mb-3">
                                                        {response ? <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                                                            <span className={`badge ${responseEnabled ? "bg-success" : "bg-secondary"}`}>{responseEnabled ? response.activeLabel : response.inactiveLabel}</span>
                                                            {responseEnabled ? <div className="btn-group flex-nowrap" role="group" aria-label={criterion.description}>
                                                                <button type="button" className={`btn btn-sm ${responseHasValue && !responseValue ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => onBooleanChange(response.id, false)}>{t`No`}</button>
                                                                <button type="button" className={`btn btn-sm ${responseValue ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => onBooleanChange(response.id, true)}>{t`Yes`}</button>
                                                            </div> : <em className="small text-muted">{response.helpText}</em>}
                                                        </div> : null}
                                                        <div className="fw-semibold">{criterion.label}</div>
                                                        <div className="text-muted small">{criterion.description}</div>
                                                    </div>
                                                );
                                            }

                                            if (criterion.type === "boolean") {
                                                const checked = scores[criterion.id] === true;
                                                const hasValue = Object.prototype.hasOwnProperty.call(scores, criterion.id);
                                                return (
                                                    <div key={criterion.id} className="mb-3">
                                                        <div className="fll-score-calc-row">
                                                            <div className="fll-score-calc-row-label fw-semibold">{criterion.label}</div>
                                                            <div className="fll-score-calc-row-controls">
                                                                <div className="btn-group flex-nowrap" role="group" aria-label={criterion.label}>
                                                                    <button type="button" className={`btn btn-sm ${hasValue && !checked ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => onBooleanChange(criterion.id, false)}>{t`No`}</button>
                                                                    <button type="button" className={`btn btn-sm ${checked ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => onBooleanChange(criterion.id, true)}>{t`Yes`}</button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            }

                                            const value = Number(scores[criterion.id] ?? 0);
                                            const hasValue = Object.prototype.hasOwnProperty.call(scores, criterion.id);
                                            const maxQuantity = criterion.maxQuantity;
                                            const isPenalty = criterion.type === "penalty_tier";
                                            const useRadio = isPenalty || maxQuantity <= 5;
                                            const radioMax = isPenalty ? 3 : maxQuantity;

                                            return (
                                                <div key={criterion.id} className="mb-3">
                                                    <div className="fll-score-calc-row">
                                                        <div className="fll-score-calc-row-label">
                                                            <div className="fw-semibold">{criterion.label}</div>
                                                            <div className="fll-score-calc-hidden-meta-spacer" aria-hidden="true" />
                                                        </div>
                                                        <div className="fll-score-calc-row-controls">
                                                            {useRadio ? (
                                                                <div className="btn-group flex-nowrap" role="group" aria-label={criterion.label}>
                                                                    {radioOptionsForMax(radioMax, criterion.type === "linear" ? criterion.terminalOptionLabel : undefined).map(opt => (
                                                                        <button
                                                                            key={`${criterion.id}-${opt.value}`}
                                                                            type="button"
                                                                            className={`btn btn-sm ${hasValue && Math.min(value, radioMax) === opt.value ? "btn-primary" : "btn-outline-secondary"}`}
                                                                            onClick={() => onLinearChange(criterion.id, opt.value, maxQuantity)}
                                                                        >
                                                                            {opt.label}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <div className="fll-score-calc-stepper" role="group" aria-label={criterion.label}>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-dark fll-score-calc-stepper-btn"
                                                                        aria-label={t`Decrease`}
                                                                        onClick={() => onLinearChange(criterion.id, value - 1, maxQuantity)}
                                                                    >
                                                                        -
                                                                    </button>
                                                                    <input
                                                                        className="form-control form-control-sm fll-score-calc-stepper-input"
                                                                        type="text"
                                                                        inputMode="numeric"
                                                                        pattern="[0-9]*"
                                                                        value={value}
                                                                        aria-label={criterion.label}
                                                                        onFocus={event => event.currentTarget.select()}
                                                                        onChange={event => onNumericInputChange(criterion.id, event.currentTarget.value, maxQuantity)}
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-dark fll-score-calc-stepper-btn"
                                                                        aria-label={t`Increase`}
                                                                        onClick={() => onLinearChange(criterion.id, value + 1, maxQuantity)}
                                                                    >
                                                                        +
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    <div className="card mb-3 fll-gp-panel" aria-label={config.presentation.graciousProfessionalism.heading}>
                        <div className="card-body">
                            <div className="mb-2"><div className="bg-dark text-white px-2 py-1 fw-bold d-inline-block">{config.presentation.graciousProfessionalism.heading}</div></div>
                            <h3 className="mb-3">{config.presentation.graciousProfessionalism.heading}</h3>
                            <div className="fll-gp-panel__selection-row">
                                <div className="text-muted small">{config.presentation.graciousProfessionalism.description}</div>
                                <div className="fll-gp-panel__levels">
                                    {config.presentation.graciousProfessionalism.levels.map(level => <button key={level.value} type="button" className={`btn btn-sm ${gpSelection === level.value ? "btn-primary" : "btn-outline-secondary"}`} aria-pressed={gpSelection === level.value} onClick={() => setGpSelection(level.value)}>{level.label} {level.value}</button>)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </>;
}
